import React, { useState, useEffect, useRef } from 'react';
import { api, API_BASE_URL } from '../../services/api';
import { socketService } from '../../utils/socketService';
import { 
  FiMessageSquare, FiSend, FiSearch, FiUser, FiTruck, 
  FiRefreshCw, FiPlus, FiX, FiChevronRight,
  FiCheck, FiCheckCircle, FiZap, FiHelpCircle, FiMic, FiPlay, FiPause
} from 'react-icons/fi';

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'À l\'instant';
  if (minutes < 60) return `Il y a ${minutes} min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days < 7) return `Il y a ${days}j`;
  
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

// Formater l'heure pour les messages
const formatTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

/** Rôle déménageur côté API : principalement `demenageur`, parfois `mover`. */
const isAdminMoverRole = (role) => role === 'mover' || role === 'demenageur';

/** Une entrée par `id` — évite les clés React dupliquées (ex. `message_sent` + réponse API). */
function dedupeAdminMessagesById(messages) {
  if (!Array.isArray(messages)) return [];
  const sorted = [...messages].sort(
    (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
  );
  const seen = new Set();
  const out = [];
  for (const m of sorted) {
    const id = m?.id != null ? String(m.id) : '';
    if (!id) {
      out.push(m);
      continue;
    }
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(m);
  }
  return out;
}

// Composant de chargement
const Loader = ({ text = 'Chargement...' }) => (
  <div className="flex flex-col items-center justify-center p-8">
    <div className="w-12 h-12 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
    <p className="mt-4 text-yellow-500 font-medium">{text}</p>
  </div>
);

function AdminMessages() {
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, client, mover
  const [users, setUsers] = useState([]);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const lastMessageIdsRef = useRef(new Map()); // Pour suivre les derniers messages par conversation
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const audioRefs = useRef({});
  /** ID conversation côté API (évite getOrCreate à chaque poll) */
  const selectedConvBackendIdRef = useRef(null);
  const selectedConversationRef = useRef(null);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Messages prédéfinis pour le support
  const quickReplies = [
    {
      category: 'Salutations',
      icon: '👋',
      color: 'blue',
      messages: [
        'Bonjour, comment puis-je vous aider aujourd\'hui ?',
        'Bonjour, merci de nous avoir contactés. Je suis là pour vous assister.',
        'Bonjour, j\'espère que vous allez bien. En quoi puis-je vous aider ?',
        'Bonjour ! Je suis à votre disposition pour répondre à toutes vos questions.'
      ]
    },
    {
      category: 'Résolution de problèmes',
      icon: '🔧',
      color: 'orange',
      messages: [
        'Je comprends votre préoccupation. Laissez-moi vérifier cela pour vous.',
        'Merci de m\'avoir signalé ce problème. Je vais le résoudre immédiatement.',
        'Je m\'excuse pour ce désagrément. Nous allons résoudre cela au plus vite.',
        'Votre demande est en cours de traitement. Je vous tiendrai informé(e) dès que possible.',
        'J\'ai bien noté votre problème. Notre équipe technique va examiner cela.'
      ]
    },
    {
      category: 'Informations',
      icon: 'ℹ️',
      color: 'purple',
      messages: [
        'Pourriez-vous me fournir plus de détails sur votre demande ?',
        'Avez-vous d\'autres questions ? Je suis là pour vous aider.',
        'Souhaitez-vous que je vous envoie plus d\'informations par email ?',
        'Je peux vous fournir toutes les informations nécessaires.',
        'Laissez-moi vous expliquer en détail...'
      ]
    },
    {
      category: 'Suivi',
      icon: '📋',
      color: 'green',
      messages: [
        'Votre demande a été enregistrée et sera traitée dans les plus brefs délais.',
        'Je vais suivre votre dossier personnellement et vous tenir informé(e).',
        'Un membre de notre équipe vous contactera sous peu.',
        'Je vous tiendrai informé(e) de l\'avancement de votre demande.'
      ]
    },
    {
      category: 'Clôture',
      icon: '✅',
      color: 'yellow',
      messages: [
        'Merci de nous avoir contactés. N\'hésitez pas si vous avez d\'autres questions.',
        'Je suis ravi(e) d\'avoir pu vous aider. Bonne journée !',
        'Votre problème a été résolu. Y a-t-il autre chose dont vous avez besoin ?',
        'Merci pour votre patience. Avez-vous d\'autres questions ?',
        'Parfait ! N\'hésitez pas à revenir vers nous si besoin.'
      ]
    }
  ];

  // Charger toutes les conversations
  const loadConversations = async () => {
    try {
      setLoading(true);
      
      // Récupérer tous les utilisateurs (clients et déménageurs)
      const usersResponse = await api.getAllUsers();
      const allUsers = usersResponse.data || [];
      const nonAdminUsers = allUsers.filter(u => u.role !== 'admin' && u.user_type !== 'admin');
      setUsers(nonAdminUsers);

      // Essayer de récupérer les conversations admin depuis l'API
      let conversationsList = [];
      try {
        const convsResponse = await api.getAdminConversations();
        if (convsResponse && convsResponse.success && convsResponse.data) {
          conversationsList = convsResponse.data.map(conv => ({
            id: conv.id,
            userId: conv.userId || conv.user_id,
            userName: conv.userName || `${conv.user_first_name || ''} ${conv.user_last_name || ''}`.trim() || conv.user_email,
            userEmail: conv.userEmail || conv.user_email,
            userRole: conv.userRole || conv.user_role,
            lastMessage: conv.lastMessage || conv.last_message,
            lastMessageTime: conv.lastMessageTime || conv.last_message_time || conv.updated_at,
            unreadCount: conv.unreadCount || conv.unread_count || 0
          }));
        }
      } catch (error) {
        console.log('Endpoint admin conversations non disponible, utilisation de la liste des utilisateurs');
      }

      // Si aucune conversation n'a été récupérée, créer des conversations à partir des utilisateurs
      if (conversationsList.length === 0) {
        conversationsList = nonAdminUsers.map(user => ({
          id: `admin-${user.id}`,
          userId: user.id,
          userName: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
          userEmail: user.email,
          userRole: user.role || user.user_type,
          lastMessage: null,
          lastMessageTime: null,
          unreadCount: 0
        }));
      }

      // Trier par dernière activité
      conversationsList.sort((a, b) => {
        if (!a.lastMessageTime && !b.lastMessageTime) return 0;
        if (!a.lastMessageTime) return 1;
        if (!b.lastMessageTime) return -1;
        return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
      });

      setConversations(conversationsList);
    } catch (error) {
      console.error('Erreur lors du chargement des conversations:', error);
      alert('Erreur lors du chargement des conversations');
    } finally {
      setLoading(false);
    }
  };

  // Charger les messages d'une conversation
  const loadMessages = async (conversation) => {
    try {
      setLoadingMessages(true);
      setSelectedConversation(conversation);
      
      // Essayer de récupérer ou créer une conversation avec l'utilisateur
      // Pour l'admin, on utilise l'endpoint admin spécifique si disponible
      try {
        const convResponse = await api.getOrCreateAdminConversation(conversation.userId);
        if (convResponse && convResponse.success && convResponse.data) {
          const convId = convResponse.data.id;
          
          // Charger les messages de cette conversation
          const messagesResponse = await api.getAdminConversationMessages(convId);
          if (messagesResponse && messagesResponse.success && messagesResponse.data) {
            // Les messages sont déjà triés du plus ancien au plus récent par l'API
            // Si ce n'est pas le cas, on les trie
            const sortedMessages = [...messagesResponse.data].map(msg => {
              // Détection de fallback pour les messages vocaux
              // Si le contenu commence par "[VOICE_MESSAGE]", c'est un message vocal
              if (msg.content && msg.content.startsWith('[VOICE_MESSAGE]')) {
                let audioUrl = msg.content.replace('[VOICE_MESSAGE]', '').trim();
                // Si l'URL contient encore du texte, extraire seulement le chemin
                if (audioUrl.includes('/uploads/voice-messages/')) {
                  const match = audioUrl.match(/\/uploads\/voice-messages\/[^\s]+/);
                  if (match) {
                    audioUrl = match[0];
                  }
                }
                return {
                  ...msg,
                  isVoice: true,
                  audioUrl: audioUrl || msg.audioUrl || msg.audio_url,
                  audio_url: audioUrl || msg.audioUrl || msg.audio_url,
                  content: null // Ne pas afficher le contenu pour les messages vocaux
                };
              }
              return msg;
            }).sort((a, b) => {
              return new Date(a.createdAt) - new Date(b.createdAt);
            });
            setMessages(dedupeAdminMessagesById(sortedMessages));
            
            // Initialiser le dernier message ID pour cette conversation
            if (sortedMessages.length > 0) {
              const lastMessageId = sortedMessages[sortedMessages.length - 1]?.id;
              if (lastMessageId) {
                lastMessageIdsRef.current.set(conversation.id, lastMessageId);
              }
            }
            
            // Réinitialiser le compteur de messages non lus pour cette conversation
            setConversations(prev => prev.map(c => 
              c.id === conversation.id ? { ...c, unreadCount: 0 } : c
            ));
            
            // Scroller vers le bas après le chargement initial
            setTimeout(() => {
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
              }
            }, 200);
          } else {
            setMessages([]);
          }
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la conversation:', error);
        // Si l'endpoint admin n'existe pas encore, on initialise avec une liste vide
        setMessages([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Démarrer l'enregistrement vocal
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Mettre à jour le temps d'enregistrement
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Erreur lors du démarrage de l\'enregistrement:', error);
      alert('Impossible d\'accéder au microphone. Vérifiez les permissions.');
    }
  };

  // Arrêter l'enregistrement vocal
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  // Annuler l'enregistrement
  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setAudioBlob(null);
      audioChunksRef.current = [];
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      setRecordingTime(0);
    }
  };

  // Envoyer un message vocal
  const sendVoiceMessage = async () => {
    if (!audioBlob || !selectedConversation || sending) return;

    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    let optimisticMessageId = null;

    try {
      setSending(true);
      
      const formData = new FormData();
      formData.append('audio', audioBlob, `voice-${Date.now()}.webm`);
      formData.append('userId', selectedConversation.userId);
      
      // Créer un message optimiste
      optimisticMessageId = `temp-${Date.now()}`;
      const optimisticMessage = {
        id: optimisticMessageId,
        content: null,
        audioUrl: URL.createObjectURL(audioBlob),
        isVoice: true,
        senderId: currentUser.id,
        createdAt: new Date().toISOString(),
        isRead: false,
        isMine: true,
        sender: {
          id: currentUser.id,
          firstName: currentUser.first_name || 'Admin',
          lastName: currentUser.last_name || ''
        }
      };

      // Ajouter le message optimiste
      setMessages(prev => {
        const updated = [...prev, optimisticMessage];
        return updated.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });

      // Scroller vers le bas
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 50);

      // Envoyer le message vocal via l'API
      const response = await api.sendAdminVoiceMessage(formData);
      
      if (response && response.success) {
        // Remplacer le message optimiste par le vrai message
        const realMessage = response.data || response.message;
        setMessages(prev => {
          const updated = prev.map(msg => 
            msg.id === optimisticMessageId ? {
              ...realMessage,
              isMine: true,
              isVoice: true,
              audioUrl: realMessage.audioUrl || realMessage.audio_url,
              sender: {
                id: currentUser.id,
                firstName: currentUser.first_name || 'Admin',
                lastName: currentUser.last_name || ''
              }
            } : msg
          );
          return dedupeAdminMessagesById(
            updated.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
          );
        });
        
        // Nettoyer
        setAudioBlob(null);
        setRecordingTime(0);
        
        // Scroller vers le bas
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        throw new Error(response.message || 'Erreur lors de l\'envoi du message vocal');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message vocal:', error);
      if (optimisticMessageId) {
        setMessages(prev => prev.filter(msg => msg.id !== optimisticMessageId));
      }
      alert(error.message || 'Erreur lors de l\'envoi du message vocal');
    } finally {
      setSending(false);
    }
  };

  // Jouer/pause un message vocal
  const toggleAudioPlayback = (messageId, audioUrl) => {
    if (playingAudioId === messageId) {
      // Pause
      const audio = audioRefs.current[messageId];
      if (audio) {
        audio.pause();
      }
      setPlayingAudioId(null);
    } else {
      // Stop l'audio en cours
      if (playingAudioId) {
        const currentAudio = audioRefs.current[playingAudioId];
        if (currentAudio) {
          currentAudio.pause();
          currentAudio.currentTime = 0;
        }
      }
      
      // Jouer le nouvel audio
      const audio = new Audio(audioUrl);
      audioRefs.current[messageId] = audio;
      
      audio.onended = () => {
        setPlayingAudioId(null);
      };
      
      audio.onerror = (error) => {
        console.error('Erreur lors de la lecture audio:', error);
        setPlayingAudioId(null);
        alert('Erreur lors de la lecture du message vocal');
      };
      
      audio.play().catch(error => {
        console.error('Erreur lors de la lecture:', error);
        setPlayingAudioId(null);
        alert('Impossible de lire le message vocal');
      });
      setPlayingAudioId(messageId);
    }
  };

  // Nettoyer les références audio au démontage
  useEffect(() => {
    // Copier la référence au début de l'effet pour éviter le warning du linter
    const currentAudioRefs = audioRefs.current;
    return () => {
      // Arrêter tous les audios en cours
      Object.values(currentAudioRefs).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      // Nettoyer l'enregistrement en cours
      const currentMediaRecorder = mediaRecorderRef.current;
      if (currentMediaRecorder) {
        try {
          currentMediaRecorder.stop();
          if (currentMediaRecorder.stream) {
            currentMediaRecorder.stream.getTracks().forEach(track => track.stop());
          }
        } catch (e) {
          // Ignorer les erreurs
        }
      }
      const currentInterval = recordingIntervalRef.current;
      if (currentInterval) {
        clearInterval(currentInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Envoyer un message
  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation || sending) return;

    try {
      setSending(true);
      const content = messageInput.trim();
      setMessageInput('');

      // Créer un message optimiste
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        content,
        senderId: currentUser.id,
        createdAt: new Date().toISOString(),
        isRead: false,
        isMine: true,
        sender: {
          id: currentUser.id,
          firstName: currentUser.first_name || 'Admin',
          lastName: currentUser.last_name || ''
        }
      };

      // Ajouter le message optimiste à la fin (nouveaux messages en bas)
      setMessages(prev => {
        const updated = [...prev, optimisticMessage];
        // S'assurer que les messages restent triés
        return updated.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });

      // Scroller vers le bas immédiatement après l'ajout du message optimiste
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 50);

      // Envoyer le message via l'API admin
      try {
        const response = await api.sendAdminMessage(selectedConversation.userId, content);
        if (response && response.success) {
          // Remplacer le message optimiste par le vrai message
          const realMessage = response.data || response.message;
          setMessages(prev => {
            const updated = prev.map(msg => 
              msg.id === optimisticMessage.id ? {
                ...realMessage,
                isMine: true,
                sender: {
                  id: currentUser.id,
                  firstName: currentUser.first_name || 'Admin',
                  lastName: currentUser.last_name || ''
                }
              } : msg
            );
            // S'assurer que les messages sont triés du plus ancien au plus récent
            const sorted = dedupeAdminMessagesById(
              updated.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            );
            
            // Mettre à jour le dernier message ID
            if (sorted.length > 0 && selectedConversation) {
              const lastMessageId = sorted[sorted.length - 1]?.id;
              if (lastMessageId) {
                lastMessageIdsRef.current.set(selectedConversation.id, lastMessageId);
              }
            }
            
            return sorted;
          });
          
          // Scroller vers le bas après la mise à jour
          setTimeout(() => {
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        } else {
          throw new Error(response.message || 'Erreur lors de l\'envoi du message');
        }
      } catch (error) {
        // En cas d'erreur, retirer le message optimiste
        setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
        throw error;
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      alert(error.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  // Filtrer les conversations
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         conv.userEmail.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'client' && conv.userRole === 'client') ||
                         (filterType === 'mover' && isAdminMoverRole(conv.userRole));
    return matchesSearch && matchesFilter;
  });

  // Scroll vers le bas des messages quand de nouveaux messages arrivent
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0 && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      // Vérifier si l'utilisateur est proche du bas (dans les 100px)
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      
      // Scroller seulement si l'utilisateur est déjà en bas
      if (isNearBottom) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [messages]);

  // Charger les conversations au montage
  useEffect(() => {
    loadConversations();
  }, []);

  /** Temps réel : messages déménageur → admin (room user_<adminId>, événement new_message). */
  useEffect(() => {
    const getStoredAdmin = () => {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}');
      } catch {
        return {};
      }
    };

    const mapWsToMessage = (raw) => {
      const admin = getStoredAdmin();
      const adminId = admin.id;
      const content = raw.content;
      const isVoice =
        content &&
        typeof content === 'string' &&
        content.startsWith('[VOICE_MESSAGE]');
      let audioUrl = null;
      if (isVoice) {
        let u = content.replace('[VOICE_MESSAGE]', '').trim();
        const match = u.match(/\/uploads\/voice-messages\/[^\s]+/);
        if (match) u = match[0];
        audioUrl = u;
      }
      const senderId = raw.sender_id || raw.senderId;
      const name = (raw.senderName || '').trim();
      const parts = name.split(/\s+/);
      return {
        id: raw.id || raw._id,
        content: isVoice ? null : content,
        audioUrl,
        audio_url: audioUrl,
        isVoice: !!isVoice,
        createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
        isRead: false,
        isMine: String(senderId) === String(adminId),
        sender: {
          id: senderId,
          firstName: parts[0] || name || 'Utilisateur',
          lastName: parts.slice(1).join(' ') || '',
        },
      };
    };

    const bumpSidebar = (otherUserId, preview, { incomingToAdmin } = {}) => {
      if (!otherUserId) return;
      setConversations((prev) => {
        const sel = selectedConversationRef.current;
        const time = new Date().toISOString();
        const next = prev.map((c) => {
          if (String(c.userId) !== String(otherUserId)) return c;
          const isSelected = sel && String(sel.userId) === String(otherUserId);
          const bumpUnread = incomingToAdmin && !isSelected;
          return {
            ...c,
            lastMessage: preview || 'Nouveau message',
            lastMessageTime: time,
            unreadCount: bumpUnread ? (c.unreadCount || 0) + 1 : isSelected ? 0 : c.unreadCount || 0,
          };
        });
        return next.sort(
          (a, b) =>
            new Date(b.lastMessageTime || 0) - new Date(a.lastMessageTime || 0)
        );
      });
    };

    const onIncoming = (payload) => {
      const raw = payload?.message;
      if (!raw) return;
      const admin = getStoredAdmin();
      const adminId = admin.id;
      if (!adminId) return;

      const senderId = String(raw.sender_id || raw.senderId || '');
      const recipientId = String(raw.recipient_id || raw.recipientId || '');

      const sel = selectedConversationRef.current;
      const isForThisThread =
        sel &&
        ((senderId === String(sel.userId) && recipientId === String(adminId)) ||
          (senderId === String(adminId) && recipientId === String(sel.userId)));

      const preview =
        raw.content && String(raw.content).startsWith('[VOICE_MESSAGE]')
          ? '🎤 Message vocal'
          : (raw.content || '').toString().slice(0, 120);

      const otherUserId =
        senderId === String(adminId) ? recipientId : senderId;
      const incomingToAdmin =
        recipientId === String(adminId) && senderId !== String(adminId);
      bumpSidebar(otherUserId, preview, { incomingToAdmin });

      if (!isForThisThread) return;

      const uiMsg = mapWsToMessage(raw);
      if (!uiMsg.id) return;

      setMessages((prev) => {
        if (prev.some((m) => String(m.id) === String(uiMsg.id))) return prev;
        const merged = dedupeAdminMessagesById([...prev, uiMsg]);
        if (sel) {
          const lastId = merged[merged.length - 1]?.id;
          if (lastId) lastMessageIdsRef.current.set(sel.id, lastId);
        }
        return merged;
      });

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 80);
    };

    socketService.connect().catch(() => {});
    const unsubNew = socketService.on('new_message', onIncoming);
    const unsubSent = socketService.on('message_sent', onIncoming);

    return () => {
      if (typeof unsubNew === 'function') unsubNew();
      if (typeof unsubSent === 'function') unsubSent();
    };
  }, []);

  // Polling de secours (socket manquant ou événement raté)
  useEffect(() => {
    if (!selectedConversation) {
      selectedConvBackendIdRef.current = null;
      return;
    }

    let cancelled = false;

    const parseVoiceMessages = (msgs) =>
      msgs.map(msg => {
        if (msg.content && msg.content.startsWith('[VOICE_MESSAGE]')) {
          let audioUrl = msg.content.replace('[VOICE_MESSAGE]', '').trim();
          const match = audioUrl.match(/\/uploads\/voice-messages\/[^\s]+/);
          if (match) audioUrl = match[0];
          return { ...msg, isVoice: true, audioUrl, audio_url: audioUrl, content: null };
        }
        return msg;
      }).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    const pollMessages = async () => {
      if (cancelled) return;
      try {
        // Résoudre l'ID backend une seule fois par conversation sélectionnée
        if (!selectedConvBackendIdRef.current) {
          const convResponse = await api.getOrCreateAdminConversation(selectedConversation.userId);
          if (cancelled) return;
          if (convResponse?.success && convResponse?.data?.id) {
            selectedConvBackendIdRef.current = convResponse.data.id;
          } else {
            return;
          }
        }

        const messagesResponse = await api.getAdminConversationMessages(selectedConvBackendIdRef.current);
        if (cancelled) return;
        if (!messagesResponse?.success || !messagesResponse?.data) return;

        const sortedMessages = parseVoiceMessages(messagesResponse.data);
        const newLastId = sortedMessages.at(-1)?.id ?? null;
        const storedLastId = lastMessageIdsRef.current.get(selectedConversation.id);

        if (newLastId !== storedLastId) {
          const isNewMsg = storedLastId !== null;
          lastMessageIdsRef.current.set(selectedConversation.id, newLastId);
          setMessages(dedupeAdminMessagesById(sortedMessages));
          if (isNewMsg && messagesContainerRef.current) {
            const c = messagesContainerRef.current;
            if (c.scrollHeight - c.scrollTop - c.clientHeight < 150) {
              setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            }
          }
        }
      } catch (err) {
        console.error('[Messages] Erreur polling:', err);
      }
    };

    // Réinitialiser l'ID backend quand la conversation change
    selectedConvBackendIdRef.current = null;
    pollMessages();
    const interval = setInterval(pollMessages, 120000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.id]);

  return (
    <div className="space-y-6 p-6 bg-gray-900 min-h-screen text-gray-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-yellow-600/30">
        <div>
          <h1 className="text-4xl font-extrabold text-yellow-400 mb-2 flex items-center">
            <FiMessageSquare className="w-8 h-8 mr-3" />
            Messages
          </h1>
          <p className="text-gray-400">Communiquez avec les clients et déménageurs</p>
        </div>
        <button
          onClick={() => setShowNewConversationModal(true)}
          className="mt-4 sm:mt-0 px-5 py-2.5 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors font-semibold flex items-center shadow-lg"
        >
          <FiPlus className="mr-2" />
          Nouvelle conversation
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: 'calc(100vh - 250px)', minHeight: '600px' }}>
          {/* Liste des conversations */}
          <div className="lg:col-span-1 bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-xl flex flex-col overflow-hidden">
            {/* En-tête */}
            <div className="p-4 border-b border-yellow-600/30 bg-gradient-to-r from-gray-900 to-gray-800">
              <h2 className="text-lg font-bold text-yellow-400 flex items-center">
                <FiMessageSquare className="mr-2" />
                Conversations
                <span className="ml-2 text-sm text-gray-400 font-normal">
                  ({filteredConversations.length})
                </span>
              </h2>
            </div>
            
            {/* Barre de recherche et filtres */}
            <div className="p-4 border-b border-yellow-600/30 bg-gray-900/50 space-y-3">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                <input
                  type="text"
                  placeholder="Rechercher une conversation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-900/50 border border-gray-700 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterType('all')}
                  className={`flex-1 px-3 py-2 text-xs font-bold rounded-lg transition-all duration-300 ${
                    filterType === 'all'
                      ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-gray-900 shadow-lg shadow-yellow-600/30 scale-105'
                      : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 hover:text-white border border-gray-600/50'
                  }`}
                >
                  Tous
                </button>
                <button
                  onClick={() => setFilterType('client')}
                  className={`flex-1 px-3 py-2 text-xs font-bold rounded-lg transition-all duration-300 flex items-center justify-center ${
                    filterType === 'client'
                      ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-600/30 scale-105'
                      : 'bg-gray-700/50 text-gray-300 hover:bg-blue-600/20 hover:text-blue-300 border border-gray-600/50'
                  }`}
                >
                  <FiUser className="mr-1.5" />
                  Clients
                </button>
                <button
                  onClick={() => setFilterType('mover')}
                  className={`flex-1 px-3 py-2 text-xs font-bold rounded-lg transition-all duration-300 flex items-center justify-center ${
                    filterType === 'mover'
                      ? 'bg-gradient-to-r from-green-600 to-green-500 text-white shadow-lg shadow-green-600/30 scale-105'
                      : 'bg-gray-700/50 text-gray-300 hover:bg-green-600/20 hover:text-green-300 border border-gray-600/50'
                  }`}
                >
                  <FiTruck className="mr-1.5" />
                  Déménageurs
                </button>
              </div>
            </div>

            {/* Liste des conversations */}
            <div className="flex-1 overflow-y-auto p-2">
              {filteredConversations.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <FiMessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune conversation trouvée</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredConversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => loadMessages(conv)}
                      className={`w-full group relative p-4 rounded-xl transition-all duration-300 text-left ${
                        selectedConversation?.id === conv.id 
                          ? 'bg-gradient-to-r from-yellow-600/30 to-yellow-600/10 border-2 border-yellow-500/50 shadow-lg shadow-yellow-600/20' 
                          : 'bg-gray-700/30 border border-gray-700 hover:bg-gray-700/50 hover:border-yellow-600/30 hover:shadow-lg'
                      }`}
                    >
                      {/* Indicateur de sélection */}
                      {selectedConversation?.id === conv.id && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-yellow-500 rounded-r-full"></div>
                      )}
                      
                      <div className="flex items-start space-x-3">
                        {/* Avatar avec badge */}
                        <div className="relative flex-shrink-0">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-transform duration-300 ${
                            conv.userRole === 'client' 
                              ? 'bg-gradient-to-br from-blue-500/30 to-blue-600/20 border-2 border-blue-500/50' 
                              : 'bg-gradient-to-br from-green-500/30 to-green-600/20 border-2 border-green-500/50'
                          } ${selectedConversation?.id === conv.id ? 'scale-110' : 'group-hover:scale-105'}`}>
                            {conv.userRole === 'client' ? (
                              <FiUser className="w-7 h-7 text-blue-400" />
                            ) : (
                              <FiTruck className="w-7 h-7 text-green-400" />
                            )}
                          </div>
                          {/* Badge de statut (toujours actif pour l'admin) */}
                          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 border-2 border-gray-800 rounded-full"></div>
                        </div>
                        
                        {/* Contenu de la conversation */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <p className={`font-bold truncate transition-colors ${
                                selectedConversation?.id === conv.id 
                                  ? 'text-yellow-400' 
                                  : 'text-white group-hover:text-yellow-400'
                              }`}>
                                {conv.userName}
                              </p>
                              <span className={`text-xs px-2 py-0.5 rounded-md font-semibold flex-shrink-0 ${
                                conv.userRole === 'client'
                                  ? 'bg-blue-600/30 text-blue-300 border border-blue-500/30'
                                  : 'bg-green-600/30 text-green-300 border border-green-500/30'
                              }`}>
                                {conv.userRole === 'client' ? 'Client' : 'Déménageur'}
                              </span>
                            </div>
                            
                            {/* Temps et badge non lus */}
                            <div className="flex flex-col items-end space-y-1 flex-shrink-0 ml-2">
                              {conv.lastMessageTime && (
                                <span className={`text-xs font-medium whitespace-nowrap ${
                                  selectedConversation?.id === conv.id 
                                    ? 'text-yellow-300/70' 
                                    : 'text-gray-400'
                                }`}>
                                  {formatDate(conv.lastMessageTime)}
                                </span>
                              )}
                              {conv.unreadCount > 0 && (
                                <span className="bg-yellow-500 text-gray-900 text-xs font-bold px-2.5 py-1 rounded-full shadow-lg animate-pulse">
                                  {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-400 truncate mb-2">{conv.userEmail}</p>
                          
                          {/* Dernier message */}
                          {conv.lastMessage ? (
                            <div className="flex items-center space-x-2">
                              <p className={`text-sm truncate flex-1 ${
                                selectedConversation?.id === conv.id 
                                  ? 'text-gray-200' 
                                  : 'text-gray-300 group-hover:text-gray-200'
                              }`}>
                                {conv.lastMessage}
                              </p>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 italic">Aucun message</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Effet de brillance au survol */}
                      {selectedConversation?.id !== conv.id && (
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Zone de chat */}
          <div className="lg:col-span-2 bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-xl flex flex-col overflow-hidden" style={{ maxHeight: '100%' }}>
            {!selectedConversation ? (
              <div className="flex-1 flex items-center justify-center min-h-0">
                <div className="text-center">
                  <FiMessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">Sélectionnez une conversation pour commencer</p>
                </div>
              </div>
            ) : (
              <>
                {/* En-tête de la conversation */}
                <div className="p-4 border-b border-yellow-600/30 bg-gradient-to-r from-gray-900 to-gray-800 flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
                      selectedConversation.userRole === 'client' 
                        ? 'bg-gradient-to-br from-blue-500/30 to-blue-600/20 border-2 border-blue-500/50' 
                        : 'bg-gradient-to-br from-green-500/30 to-green-600/20 border-2 border-green-500/50'
                    }`}>
                      {selectedConversation.userRole === 'client' ? (
                        <FiUser className="w-6 h-6 text-blue-400" />
                      ) : (
                        <FiTruck className="w-6 h-6 text-green-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="font-bold text-white text-lg">{selectedConversation.userName}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-md font-semibold ${
                          selectedConversation.userRole === 'client'
                            ? 'bg-blue-600/30 text-blue-300 border border-blue-500/30'
                            : 'bg-green-600/30 text-green-300 border border-green-500/30'
                        }`}>
                          {selectedConversation.userRole === 'client' ? 'Client' : 'Déménageur'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{selectedConversation.userEmail}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-gray-500">En ligne</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedConversation(null);
                      setMessages([]);
                      setShowQuickReplies(false);
                    }}
                    className="lg:hidden text-gray-400 hover:text-yellow-400 transition-colors p-2 hover:bg-gray-700 rounded-lg"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {/* Messages */}
                <div 
                  ref={messagesContainerRef}
                  className="flex-1 overflow-y-auto p-4 min-h-0"
                  style={{ display: 'flex', flexDirection: 'column', maxHeight: '100%' }}
                >
                  {loadingMessages ? (
                    <Loader text="Chargement des messages..." />
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <FiMessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400">Aucun message pour le moment</p>
                        <p className="text-sm text-gray-500 mt-1">Envoyez le premier message !</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col space-y-4">
                      {messages.map((message, index) => {
                        const isMine = message.isMine || message.senderId === JSON.parse(localStorage.getItem('user') || '{}').id;
                        const prevMessage = index > 0 ? messages[index - 1] : null;
                        const showAvatar = !prevMessage || prevMessage.senderId !== message.senderId || 
                                         new Date(message.createdAt) - new Date(prevMessage.createdAt) > 300000; // 5 minutes
                      
                      return (
                        <div
                          key={message.id}
                          className={`flex ${isMine ? 'justify-end' : 'justify-start'} ${
                            showAvatar ? 'mt-4' : 'mt-1'
                          }`}
                        >
                          {!isMine && (
                            <div className="flex-shrink-0 mr-2">
                              {showAvatar ? (
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md ${
                                  selectedConversation.userRole === 'client'
                                    ? 'bg-gradient-to-br from-blue-500/30 to-blue-600/20 border border-blue-500/50'
                                    : 'bg-gradient-to-br from-green-500/30 to-green-600/20 border border-green-500/50'
                                }`}>
                                  {selectedConversation.userRole === 'client' ? (
                                    <FiUser className="w-5 h-5 text-blue-400" />
                                  ) : (
                                    <FiTruck className="w-5 h-5 text-green-400" />
                                  )}
                                </div>
                              ) : (
                                <div className="w-10 h-10" />
                              )}
                            </div>
                          )}
                          <div className={`max-w-[75%] ${isMine ? 'order-2' : ''}`}>
                            {!isMine && showAvatar && (
                              <div className="flex items-center space-x-2 mb-1.5 px-2">
                                <p className="text-xs font-semibold text-gray-300">
                                  {message.sender?.firstName || selectedConversation.userName}
                                </p>
                                <span className="text-xs text-gray-500">•</span>
                                <span className="text-xs text-gray-500">{formatTime(message.createdAt)}</span>
                              </div>
                            )}
                            <div
                              className={`px-4 py-3 rounded-xl shadow-lg ${
                                isMine
                                  ? 'bg-gradient-to-br from-yellow-600 to-yellow-500 text-gray-900'
                                  : 'bg-gradient-to-br from-gray-700 to-gray-800 text-white border border-gray-700'
                              }`}
                            >
                              {/* Message vocal */}
                              {(message.isVoice || message.audioUrl || message.audio_url || 
                                (message.content && message.content.startsWith('[VOICE_MESSAGE]'))) ? (
                                <div className="flex items-center space-x-3">
                                  <button
                                    onClick={() => {
                                      let audioUrl = message.audioUrl || message.audio_url;
                                      
                                      // Si le contenu commence par "[VOICE_MESSAGE]", extraire l'URL
                                      if (!audioUrl && message.content && message.content.startsWith('[VOICE_MESSAGE]')) {
                                        audioUrl = message.content.replace('[VOICE_MESSAGE]', '').trim();
                                      }
                                      
                                      // Si toujours pas d'URL, essayer d'extraire depuis le contenu complet
                                      if (!audioUrl && message.content && message.content.includes('/uploads/voice-messages/')) {
                                        const match = message.content.match(/\/uploads\/voice-messages\/[^\s]+/);
                                        if (match) {
                                          audioUrl = match[0];
                                        }
                                      }
                                      
                                      // Construire l'URL complète si c'est un chemin relatif
                                      let fullUrl = null;
                                      if (audioUrl) {
                                        if (audioUrl.startsWith('http://') || audioUrl.startsWith('https://')) {
                                          fullUrl = audioUrl;
                                        } else {
                                          // Base HTTP sans suffixe /api (ex. http://host:3000/api -> http://host:3000)
                                          const baseUrl = API_BASE_URL.replace(/\/api\/?$/, '');
                                          fullUrl = `${baseUrl}${audioUrl.startsWith('/') ? audioUrl : '/' + audioUrl}`;
                                        }
                                      }
                                      
                                      console.log('[Voice Message] Audio URL:', { audioUrl, fullUrl, message });
                                      
                                      if (fullUrl) {
                                        toggleAudioPlayback(message.id, fullUrl);
                                      } else {
                                        console.error('[Voice Message] Impossible de trouver l\'URL audio:', message);
                                        alert('Impossible de lire le message vocal. URL audio introuvable.');
                                      }
                                    }}
                                    className={`flex items-center justify-center w-12 h-12 rounded-full transition-all ${
                                      playingAudioId === message.id
                                        ? 'bg-red-500/20 text-red-400 border-2 border-red-500/50'
                                        : isMine
                                        ? 'bg-yellow-700/30 text-gray-900 border-2 border-yellow-700/50 hover:bg-yellow-700/40'
                                        : 'bg-gray-600/30 text-white border-2 border-gray-600/50 hover:bg-gray-600/40'
                                    }`}
                                  >
                                    {playingAudioId === message.id ? (
                                      <FiPause className="w-6 h-6" />
                                    ) : (
                                      <FiPlay className="w-6 h-6 ml-1" />
                                    )}
                                  </button>
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <FiMic className={`w-4 h-4 ${isMine ? 'text-gray-700' : 'text-gray-300'}`} />
                                      <span className={`text-sm font-medium ${isMine ? 'text-gray-700' : 'text-gray-300'}`}>
                                        Message vocal
                                      </span>
                                    </div>
                                    {message.duration && (
                                      <span className={`text-xs ${isMine ? 'text-gray-600' : 'text-gray-400'}`}>
                                        {Math.floor(message.duration / 60)}:{(message.duration % 60).toString().padStart(2, '0')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
                              )}
                              {isMine && (
                                <div className="flex items-center justify-end mt-2 space-x-2 text-gray-800/70">
                                  <span className="text-xs font-medium">{formatTime(message.createdAt)}</span>
                                  <span className="flex items-center">
                                    {message.isRead ? (
                                      <FiCheckCircle className="w-3.5 h-3.5 text-blue-500" title="Message lu" />
                                    ) : (
                                      <FiCheck className="w-3.5 h-3.5" title="Message envoyé" />
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          {isMine && (
                            <div className="flex-shrink-0 ml-2">
                              <div className="w-8 h-8" />
                            </div>
                          )}
                        </div>
                          );
                        })}
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Zone de saisie */}
                <div className="border-t border-yellow-600/30 bg-gradient-to-b from-gray-900 to-gray-800 flex-shrink-0">
                  {/* Indicateur d'enregistrement vocal */}
                  {isRecording && (
                    <div className="px-4 py-3 bg-red-600/20 border-b border-red-600/30 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-semibold text-red-400">
                          Enregistrement en cours...
                        </span>
                        <span className="text-xs text-gray-400 font-mono">
                          {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={stopRecording}
                          className="px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all font-semibold text-sm"
                        >
                          Arrêter
                        </button>
                        <button
                          onClick={cancelRecording}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-all font-semibold text-sm"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Aperçu du message vocal enregistré */}
                  {audioBlob && !isRecording && (
                    <div className="px-4 py-3 bg-yellow-600/20 border-b border-yellow-600/30 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <FiMic className="w-5 h-5 text-yellow-400" />
                        <span className="text-sm font-semibold text-yellow-400">
                          Message vocal enregistré
                        </span>
                        <span className="text-xs text-gray-400 font-mono">
                          {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            const audio = new Audio(URL.createObjectURL(audioBlob));
                            audio.play();
                          }}
                          className="px-3 py-1.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-all text-sm flex items-center space-x-1"
                        >
                          <FiPlay className="w-4 h-4" />
                          <span>Écouter</span>
                        </button>
                        <button
                          onClick={() => {
                            setAudioBlob(null);
                            setRecordingTime(0);
                          }}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-all text-sm"
                        >
                          Supprimer
                        </button>
                        <button
                          onClick={sendVoiceMessage}
                          disabled={sending}
                          className="px-4 py-1.5 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all font-semibold text-sm disabled:opacity-50"
                        >
                          {sending ? 'Envoi...' : 'Envoyer'}
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Messages prédéfinis */}
                  {showQuickReplies && (
                    <div className="border-b border-yellow-600/30 bg-gradient-to-b from-gray-800 to-gray-900">
                      <div className="p-4 border-b border-yellow-600/30 bg-gray-800/80 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="p-2 bg-yellow-600/20 rounded-lg border border-yellow-500/50">
                            <FiZap className="w-5 h-5 text-yellow-400" />
                          </div>
                          <div>
                            <h3 className="text-sm font-bold text-yellow-400">Réponses rapides</h3>
                            <p className="text-xs text-gray-400">Sélectionnez un message prédéfini</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowQuickReplies(false)}
                          className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-lg transition-all"
                        >
                          <FiX className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="p-4 max-h-80 overflow-y-auto" style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#eab308 rgba(0,0,0,0.1)'
                      }}>
                        <div className="space-y-4">
                          {quickReplies.map((category, catIndex) => {
                            const colorClasses = {
                              blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-300',
                              orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/30 text-orange-300',
                              purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-300',
                              green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-300',
                              yellow: 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30 text-yellow-300'
                            };
                            
                            return (
                              <div key={catIndex} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700">
                                <div className={`flex items-center space-x-2 mb-3 px-3 py-2 rounded-lg bg-gradient-to-r ${colorClasses[category.color] || colorClasses.blue} border`}>
                                  <span className="text-lg">{category.icon}</span>
                                  <p className="text-xs font-bold uppercase tracking-wider">
                                    {category.category}
                                  </p>
                                </div>
                                <div className="grid grid-cols-1 gap-2">
                                  {category.messages.map((msg, msgIndex) => (
                                    <button
                                      key={msgIndex}
                                      onClick={async () => {
                                        if (!selectedConversation || sending) return;
                                        
                                        // Fermer le panneau des messages prédéfinis
                                        setShowQuickReplies(false);
                                        
                                        // Envoyer directement le message
                                        let optimisticMessageId = null;
                                        try {
                                          setSending(true);
                                          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                                          
                                          // Créer un message optimiste
                                          optimisticMessageId = `temp-${Date.now()}`;
                                          const optimisticMessage = {
                                            id: optimisticMessageId,
                                            content: msg,
                                            senderId: currentUser.id,
                                            createdAt: new Date().toISOString(),
                                            isRead: false,
                                            isMine: true,
                                            sender: {
                                              id: currentUser.id,
                                              firstName: currentUser.first_name || 'Admin',
                                              lastName: currentUser.last_name || ''
                                            }
                                          };
                                          
                                          // Ajouter le message optimiste
                                          setMessages(prev => {
                                            const updated = [...prev, optimisticMessage];
                                            return updated.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                                          });
                                          
                                          // Scroller vers le bas
                                          setTimeout(() => {
                                            if (messagesEndRef.current) {
                                              messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                                            }
                                          }, 50);
                                          
                                          // Envoyer le message via l'API
                                          const response = await api.sendAdminMessage(selectedConversation.userId, msg);
                                          
                                          if (response && response.success) {
                                            // Remplacer le message optimiste par le vrai message
                                            const realMessage = response.data || response.message;
                                            setMessages(prev => {
                                              const updated = prev.map(message => 
                                                message.id === optimisticMessageId ? {
                                                  ...realMessage,
                                                  isMine: true,
                                                  sender: {
                                                    id: currentUser.id,
                                                    firstName: currentUser.first_name || 'Admin',
                                                    lastName: currentUser.last_name || ''
                                                  }
                                                } : message
                                              );
                                              return dedupeAdminMessagesById(
                                                updated.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                                              );
                                            });
                                            
                                            // Scroller vers le bas après la mise à jour
                                            setTimeout(() => {
                                              if (messagesEndRef.current) {
                                                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                                              }
                                            }, 100);
                                          } else {
                                            throw new Error(response.message || 'Erreur lors de l\'envoi du message');
                                          }
                                        } catch (error) {
                                          console.error('Erreur lors de l\'envoi du message prédéfini:', error);
                                          // Retirer le message optimiste en cas d'erreur
                                          if (optimisticMessageId) {
                                            setMessages(prev => prev.filter(m => m.id !== optimisticMessageId));
                                          }
                                          alert(error.message || 'Erreur lors de l\'envoi du message');
                                        } finally {
                                          setSending(false);
                                        }
                                      }}
                                      disabled={!selectedConversation || sending}
                                      className="group w-full text-left px-4 py-3 text-sm bg-gray-700/30 hover:bg-gradient-to-r hover:from-yellow-600/20 hover:to-yellow-500/10 border border-gray-700 hover:border-yellow-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-200 hover:shadow-lg hover:shadow-yellow-600/10 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                    >
                                      <div className="flex items-start space-x-2">
                                        <span className="text-yellow-400/0 group-hover:text-yellow-400 transition-colors mt-0.5">→</span>
                                        <span className="flex-1">{msg}</span>
                                        {sending && (
                                          <FiRefreshCw className="w-4 h-4 animate-spin text-yellow-400" />
                                        )}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="p-4">
                    <div className="flex items-end space-x-3">
                      {/* Bouton messages prédéfinis */}
                      <button
                        onClick={() => setShowQuickReplies(!showQuickReplies)}
                        className={`px-4 py-2.5 rounded-lg transition-all flex items-center justify-center shadow-lg ${
                          showQuickReplies
                            ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-gray-900 shadow-yellow-600/30 scale-105'
                            : 'bg-gray-700 text-gray-300 hover:bg-gradient-to-r hover:from-yellow-600/20 hover:to-yellow-500/10 hover:text-yellow-400 border border-gray-600 hover:border-yellow-500/50'
                        }`}
                        title="Réponses rapides"
                      >
                        <FiZap className={`w-5 h-5 ${showQuickReplies ? 'animate-pulse' : ''}`} />
                      </button>
                      
                      {/* Bouton microphone */}
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={sending || audioBlob}
                        className={`p-2.5 rounded-lg transition-all flex-shrink-0 ${
                          isRecording
                            ? 'bg-red-600 text-white hover:bg-red-500 animate-pulse'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-yellow-400'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={isRecording ? 'Arrêter l\'enregistrement' : 'Enregistrer un message vocal'}
                      >
                        <FiMic className="w-5 h-5" />
                      </button>
                      
                      <div className="flex-1 relative">
                        <textarea
                          value={messageInput}
                          onChange={(e) => setMessageInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          placeholder="Tapez votre message de support..."
                          rows={1}
                          disabled={isRecording || audioBlob}
                          className="w-full px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ minHeight: '44px', maxHeight: '120px' }}
                        />
                        <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                          {messageInput.length > 0 && `${messageInput.length} caractères`}
                        </div>
                      </div>
                      
                      <button
                        onClick={sendMessage}
                        disabled={!messageInput.trim() || sending || isRecording || audioBlob}
                        className="px-5 py-2.5 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-semibold shadow-lg"
                        title="Envoyer le message"
                      >
                        {sending ? (
                          <FiRefreshCw className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <FiSend className="w-5 h-5 mr-1" />
                            Envoyer
                          </>
                        )}
                      </button>
                    </div>
                    
                    {/* Indicateur de frappe professionnel */}
                    <div className="mt-2 flex items-center space-x-2 text-xs text-gray-500">
                      <FiHelpCircle className="w-3 h-3" />
                      <span>Astuce : Utilisez les réponses rapides pour un support plus efficace</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal pour nouvelle conversation */}
      {showNewConversationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-yellow-600/30 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-yellow-400">Nouvelle conversation</h2>
              <button
                onClick={() => {
                  setShowNewConversationModal(false);
                  setModalSearchQuery('');
                }}
                className="text-gray-400 hover:text-yellow-400 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur..."
                  value={modalSearchQuery}
                  onChange={(e) => setModalSearchQuery(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>
              <div className="space-y-2">
                {users
                  .filter(u => 
                    `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
                    u.email.toLowerCase().includes(modalSearchQuery.toLowerCase())
                  )
                  .map((user) => (
                    <button
                      key={user.id}
                      onClick={() => {
                        const newConv = {
                          id: `admin-${user.id}`,
                          userId: user.id,
                          userName: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
                          userEmail: user.email,
                          userRole: user.role || user.user_type,
                          lastMessage: null,
                          lastMessageTime: null,
                          unreadCount: 0
                        };
                        setConversations(prev => {
                          const exists = prev.find(c => c.id === newConv.id);
                          return exists ? prev : [newConv, ...prev];
                        });
                        loadMessages(newConv);
                        setShowNewConversationModal(false);
                        setModalSearchQuery('');
                      }}
                      className="w-full p-4 bg-gray-800/50 border border-gray-700 rounded-lg hover:bg-gray-700/50 hover:border-yellow-600/30 transition-all text-left flex items-center space-x-3"
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        (user.role || user.user_type) === 'client' 
                          ? 'bg-blue-600/20 border border-blue-500/50' 
                          : 'bg-green-600/20 border border-green-500/50'
                      }`}>
                        {(user.role || user.user_type) === 'client' ? (
                          <FiUser className="w-6 h-6 text-blue-400" />
                        ) : (
                          <FiTruck className="w-6 h-6 text-green-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-white">
                          {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                        </p>
                        <p className="text-sm text-gray-400">{user.email}</p>
                      </div>
                      <FiChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMessages;

